import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography } from '../typography/Typography';
import useTable from '../private/components/table/useTable';
import useTableHead from '../private/components/table/useTableHead';
import { SortAsc } from '../private/icons/SortAsc';
import { ButtonBase } from '../private/components/button/ButtonBase';

/**
 * `TableSortLabel` provides default styling for sorting [`Table`](#table) rows using column headers.
 * Make sure to remove padding in the containing parent `TableCell` component.
 *
 * Usage:
 *
 * ```jsx
 * import { TableSortLabel } from '@one-thd/sui-atomic-components';
 * ```
 */
const TableSortLabel = React.forwardRef((props, ref) => {
  const {
    active = false,
    children,
    direction = 'asc',
    hideSortIcon = false,
    ...other
  } = props;

  const table = useTable();
  const tableHead = useTableHead();
  const density = tableHead?.density || table?.density || 'normal';
  const classes = classNames('sui-group sui-table-sort-label', {
    'sui-py-1': density === 'tight',
    'sui-py-2': density === 'normal' || !density,
    'sui-py-4': density === 'loose'
  });

  const iconClasses = classNames('sui-flex sui-transition-transform sui-duration-250 sui-fill-primary', {
    'sui-rotate-0': direction === 'asc',
    'sui-rotate-180': direction === 'desc',
    'sui-opacity-0 group-hover:sui-opacity-100 group-focus-visible:sui-opacity-100': !active
  });

  return (
    <ButtonBase
      unstyled
      className={classes}
      ref={ref}
      role="button"
      {...other}
    >
      <Typography
        component="span"
        variant="body-base"
        weight="bold"
        height="tight"
      >
        {children}
      </Typography>
      {hideSortIcon && !active ? null : (
        <div className={iconClasses}>
          <SortAsc size="small" color="inherit" />
        </div>
      )}
    </ButtonBase>
  );
});

TableSortLabel.displayName = 'TableSortLabel';

TableSortLabel.propTypes = {
  /**
   * If `true`, the label will have the active styling (e.g. sort icon displays).
   * @default false
   */
  active: PropTypes.bool,
  /**
   * The content of the component.
   */
  children: PropTypes.string,
  /**
   * The current sort direction.
   * @default 'asc'
   */
  direction: PropTypes.oneOf(['asc', 'desc']),
  /**
   * Hide sort icon when active is false.
   * @default false
   */
  hideSortIcon: PropTypes.bool,
};

export { TableSortLabel };
